import React, { useMemo } from "react";
import * as d3 from "d3";
import { useTheme } from "@mui/material";

import customChordLayout from "./CustomChordLayout";

/**
 * Chord sorting and gradient coloring based off of the blog "Data-based orientations for gradients
 * in a d3.js chord diagram" found at the link below.
 * https://www.visualcinnamon.com/2016/06/orientation-gradient-d3-chord-diagram/
 */

interface Group {
  endAngle: number;
  index: number;
  startAngle: number;
  value: number;
}

interface ChordNode extends Group {
  subindex: number;
}

interface Chord {
  source: ChordNode;
  target: ChordNode;
}

interface ChordChartContentProps {
  matrix: any;
  onHover?(index: number | undefined, ribbon?: Record<string, any> | undefined): void;
  ribbonColor?: "source" | "target";
}

export default function ChordChartContent(props: ChordChartContentProps): React.JSX.Element {
  const theme = useTheme();
  const colors = theme.colors.namedColors.chartColorArray;

  // Initialize chart constants.
  const chartSize = 1000;
  const chartPadding = Math.max(chartSize * 0.03, 25);
  const chartRadius = chartSize / 2 - chartPadding;
  const groupThickness = Math.max(chartSize * 0.03, 10);
  const ribbonPadding = Math.max(chartSize * 0.005, 4);

  // Initialize d3 components.
  const chord = customChordLayout()
    .padding(0.05)
    .sortChords(d3.descending)
    .matrix(props.matrix);
  const groups: Array<Group> = chord.groups();
  const chords: Array<Chord> = chord.chords();
  const arc = d3.arc();
  const ribbon = d3.ribbon();

  // Uncomment to use with arc labels
  // const hiddenArcPaths = [];

  // Define helper functions.
  function getArcId(index: number) {
    return `arc-${index}`;
  }
  function getGradientId(chord: Chord) {
    return `ribbonGradient-${chord.source.index}-${chord.target.index}`
  }
  /**
   * TODO_ARC_LABELS
   * The commented out code below is an initial implementation of group labels on the outside of 
   * the group arcs.  This could be used as an alternative version of a legend.  The implementation 
   * is not complete and will require moderate calculations to be able to trim the dynamic group 
   * labels based on the available group size - it is possible to have a long group name but a very 
   * small arc due to few results.
   */
  // function createHiddenTextPath(g: Group, arcPath: string, index: number) {
  //   const firstArcSection = /(^.+?)L/;
  //   let newArc = arcPath && firstArcSection.exec(arcPath)?.[1] || "";
  //   newArc = newArc.replace(/,/g , " ");
  //   if (g.startAngle !== 0 && g.endAngle > 90 * Math.PI / 180 && g.startAngle < 270 * Math.PI / 180) {
  //     const startLoc = /M(.*?)A/;
  //     const middleLoc = /A(.*?)0 0 1/;
  //     const endLoc = /0 0 1 (.*?)$/;

  //     const newStart = endLoc.exec(newArc)?.[1];
  //     const newEnd = startLoc.exec(newArc)?.[1];
  //     const middleSec = middleLoc.exec(newArc)?.[1];

  //     newArc = `M${newStart}A${middleSec}0 0 0 ${newEnd}`;
  //   }
  //   return <path
  //     d={newArc}
  //     id={getArcId(index)}
  //     fill="none"
  //     key={`namePath-${index}`}
  //   />
  // }
  // function createArcTextPath(groups: Array<Group>) {
  //   const textPaths = groups.map((g, index: number) => {
  //     const dy = g.startAngle !== 0 && g.endAngle > 90 * Math.PI / 180 && g.startAngle < 270 * Math.PI / 180 ? 14 : -4;
  //     return <text
  //       dy={dy}
  //       key={`name-${index}`}
  //     >
  //       <textPath
  //         startOffset="50%"
  //         textAnchor="middle"
  //         href={`#${getArcId(index)}`}
  //       >
  //         {`INDEX ${index}`}
  //       </textPath>
  //     </text>
  //   });
  //   return textPaths;
  // }

  // Use the chord groups to create the outer arcs.
  const arcPaths = useMemo(() => {
    return groups.map((g, index: number) => {
      // Create the arc path to be drawn.
      const arcPath = arc({
        innerRadius: chartRadius - groupThickness,
        outerRadius: chartRadius,
        startAngle: g.startAngle,
        endAngle: g.endAngle
      }) ?? "";

      /**
       * TODO_ARC_LABELS
       * Arc labels are rendered along hidden arcs that are created specifically for the text.
       */
      // const hiddenArcPath = createHiddenTextPath(g, arcPath, index);
      // hiddenArcPaths.push(hiddenArcPath)

      return <path
        d={arcPath}
        fill={colors[g.index % 10]}
        key={`group-${index}`}
        style={{cursor:"pointer"}}
        onMouseEnter={() => props.onHover?.(index)}
        onMouseLeave={() => props.onHover?.(undefined)}
      />
    });
  }, [groups, chartRadius, groupThickness, colors]);

  // Create labels for the arcs
  // const arcNames = createArcTextPath(groups);
  
  // The linear gradients that will be applied to each ribbon
  const linearGradients = useMemo(() => {
    return chords.map((c, index: number) => {
      const id = getGradientId(c);

      const sourceCenterAngle = (c.source.endAngle - c.source.startAngle) / 2 + c.source.startAngle - Math.PI / 2;
      const x1 = chartRadius * Math.cos(sourceCenterAngle);
      const y1 = chartRadius * Math.sin(sourceCenterAngle);

      const targetCenterAngle = (c.target.endAngle - c.target.startAngle) / 2 + c.target.startAngle - Math.PI / 2;
      const x2 = chartRadius * Math.cos(targetCenterAngle);
      const y2 = chartRadius * Math.sin(targetCenterAngle);

      const startColor = props.ribbonColor === "target" ? colors[c.target.index % 10] : colors[c.source.index % 10];
      const endColor = props.ribbonColor === "target" ? colors[c.source.index % 10] : colors[c.target.index % 10];

      return <linearGradient
        gradientUnits="userSpaceOnUse"
        key={id}
        id={id}
        x1={x1} y1={y1}
        x2={x2} y2={y2}>
        <stop offset="30%" stopColor={startColor} stopOpacity={0.7} />
        <stop offset="70%" stopColor={endColor} stopOpacity={0.7} />
      </linearGradient>
    });
  }, [chords, chartRadius, colors]);

  // The inner ribbons of the Chord chart
  const ribbonPaths = useMemo(() => {
    return chords.map((c, index: number) => {
      const ribbonPath = ribbon({
        source: {...c.source, radius: chartRadius - groupThickness - ribbonPadding},
        target: {...c.target, radius: chartRadius - groupThickness - ribbonPadding},
      });

      return <path
        key={`ribbon-${index}`}
        d={ribbonPath}
        fill={`url(#${getGradientId(c)})`}
        style={{cursor:"pointer"}}
        onMouseEnter={() => props.onHover?.(c.source.index, { source: c.source, target: c.target })}
        onMouseLeave={() => props.onHover?.(undefined)}
      />
    });
  }, [chords, chartRadius, groupThickness, ribbonPadding])
  
  return (
    <svg width="100%" height="100%" viewBox={`0 0 ${chartSize} ${chartSize}`}>
      <defs>
        {linearGradients}
      </defs>
      <g transform={`translate(${chartSize / 2}, ${chartSize / 2})`}>
        {arcPaths}
        {/* TODO_ARC_LABELS need to be uncommented to display text arcs */}
        {/* {hiddenArcPaths} */}
        {/* {arcNames} */}
        {ribbonPaths}
      </g>
    </svg>
  )
}
